import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Input from "../../../designSystem/b-molecules/formElements/input/Input";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import { onValue, ref, push, update, remove } from "firebase/database";
import { db } from "../../../configs/Firebase";
import Fuse from "fuse.js";
import style from "./ManageCountries.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";

const ManageCountries = ({ show, onClose, inline = false }) => {
  const org = useSelector((state) => state.orgStatus.value);
  const [countries, setCountries] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [newCountryName, setNewCountryName] = useState("");
  const [newCountryCode, setNewCountryCode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (org.payload && org.payload !== undefined) {
      const countriesRef = ref(db, `userData/${org.payload}/countries`);

      onValue(countriesRef, (snapshot) => {
        const data = snapshot.val();
        const temp = [];
        if (data) {
          for (let i in data) {
            temp.push({
              id: i,
              ...data[i],
            });
          }
        }
        setCountries(temp);
      });
    }
  }, [org]);

  const fuse = new Fuse(countries, {
    keys: [{ name: "name" }],
    includeMatches: true,
  });
  const [countryResults, setCountryResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  function searchHandler(e) {
    setError("");
    const value = e.target.value;
    setSearchTerm(value);
    if (value) {
      setCountryResults(fuse.search(value));
    } else {
      setCountryResults([]);
    }
  }

  function createNew() {
    if (!newCountryName.trim()) {
      setError("Country name cannot be empty");
      return;
    }
    
    // Check if country already exists
    if (countries.some(c => c.name.toLowerCase() === newCountryName.trim().toLowerCase())) {
      setError("This country already exists");
      return;
    }

    if (org.payload) {
      const countriesRef = ref(db, `userData/${org.payload}/countries`);
      const newCountry = {
        name: newCountryName.trim(),
        code: newCountryCode.trim() || null,
        createdAt: { ".sv": "timestamp" }
      };
      push(countriesRef, newCountry);
      setNewCountryName("");
      setNewCountryCode("");
      setCreatingNew(false);
      setError("");
    }
  }

  function startEdit(country) {
    setEditingCountry(country);
    setNewCountryName(country.name);
    setNewCountryCode(country.code || "");
    setCreatingNew(false);
    setError("");
  }

  function saveEdit() {
    if (!newCountryName.trim()) {
      setError("Country name cannot be empty");
      return;
    }

    // Check if another country with this name exists
    if (countries.some(c => c.id !== editingCountry.id && c.name.toLowerCase() === newCountryName.trim().toLowerCase())) {
      setError("This country name already exists");
      return;
    }

    if (org.payload && editingCountry) {
      const countryRef = ref(db, `userData/${org.payload}/countries/${editingCountry.id}`);
      update(countryRef, {
        name: newCountryName.trim(),
        code: newCountryCode.trim() || null
      });
      setEditingCountry(null);
      setNewCountryName("");
      setNewCountryCode("");
      setError("");
    }
  }

  function deleteCountry(countryId) {
    if (org.payload) {
      const countryRef = ref(db, `userData/${org.payload}/countries/${countryId}`);
      remove(countryRef);
    }
  }

  function cancel() {
    setCreatingNew(false);
    setEditingCountry(null);
    setNewCountryName("");
    setNewCountryCode("");
    setError("");
  }

  const displayCountries = searchTerm ? countryResults.map(r => r.item) : countries;

  const content = (
    <>
      <div className={style.input}>
        <Input
          label="Search Countries"
          placeholder=""
          onChange={searchHandler}
          value={searchTerm}
          error={error}
        />
      </div>

      {(creatingNew || editingCountry) ? (
        <div className={style.editSection}>
          <Input
            label="Country Name"
            placeholder="Enter country name"
            onChange={(e) => setNewCountryName(e.target.value)}
            value={newCountryName}
            error={error}
          />
          <Input
            label="Country Code (Optional)"
            placeholder="e.g., GB, US, FR"
            onChange={(e) => setNewCountryCode(e.target.value)}
            value={newCountryCode}
          />
          <div className={style.actions}>
            <Button variants={["text"]} onClick={cancel}>
              Cancel
            </Button>
            <Button
              variants={["primary"]}
              onClick={editingCountry ? saveEdit : createNew}
            >
              {editingCountry ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ul className={style.countryList}>
            {displayCountries.length > 0 ? (
              displayCountries.map((country) => (
                <li key={country.id}>
                  <span className={style.countryName}>
                    {country.name}
                    {country.code && <span className={style.countryCode}>({country.code})</span>}
                  </span>
                  <div className={style.countryActions}>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => startEdit(country)}
                    >
                      <Icon>edit</Icon>
                    </Button>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete "${country.name}"?`)) {
                          deleteCountry(country.id);
                        }
                      }}
                    >
                      <Icon>delete</Icon>
                    </Button>
                  </div>
                </li>
              ))
            ) : (
              <li className={style.noResults}>
                {searchTerm ? "No countries found" : "No countries created yet"}
              </li>
            )}
          </ul>
          <div className={style.actions}>
            <Button variants={["text"]} onClick={() => setCreatingNew(true)}>
              Create New Country
            </Button>
            {!inline && (
              <Button variants={["text"]} onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );

  if (inline) {
    return content;
  }

  return (
    <Dialog show={show} onClose={onClose} title="Manage Countries">
      {content}
    </Dialog>
  );
};

export default ManageCountries;

